import { createContext, ErrorInfo, useCallback, useContext, useEffect, useRef, useState } from "react";
import { AvailabilityType, ContractedHoursType, IAvailability, IBookableresourceAvailability, IBookableresourcecategoryResponse, ICharacteristic, IContractedHours, IExpandCalendarAvailabity, IExpandCalendarResponse, IResource, IResourceIngress, RepeatPattern } from "../interfaces/resource";
import { IProviderProps } from "../interfaces/provider";
import { apiContext } from "./api";
import { authContext } from "./auth";
import { EEventStatus } from "../helpers/calendar";
import { renderGenericFallbackUi } from "../components/error-boundary/generic-fallback-ui";
import { Icon } from "@fluentui/react";
import { addDays, addMinutes, addMonths, differenceInMinutes, endOfDay, format, isBefore, isSameDay, startOfDay, subDays, subMinutes, subMonths } from "date-fns";
import { IEvent, IEventBase } from "../interfaces/event";
import { availabilityFormatter, availabilityFortnightlyFormatter, contractedHoursFormatter, leaveFormatter } from "../helpers/shifts/shiftsFormatter";

// Track which CRM record type backs a resource.
type ResourceBacking = "systemuser" | "contact";

interface IResourceContext {
    loading: string
    getResource: (id: string) => IResource | null
    getAllResources: () => IResource[]
    getResourceCount: () => number
    getResourcesRevision: () => number
    setResources: (resources: IResource[]) => void
    updateAvailability: (availability: IAvailability[]) => void
    getAvailabilities: () => IAvailability[]
    getAvailabilityByResource: (resourceId: string) => EEventStatus;
    getLeaves(): IEvent[];
    getIlluminaAvailablities: () => IEvent[];
}

const resourcesContext = createContext<IResourceContext>({
    loading: '',
    getAllResources: () => ([]),
    getResource: () => null,
    getResourceCount: () => 0,
    getResourcesRevision: () => 0,
    setResources: (resources: IResource[]) => { },
    updateAvailability: (availability: IAvailability[]) => { },
    getAvailabilities: () => [],
    getAvailabilityByResource: (resourceId: string) => (EEventStatus.error),
    getLeaves: () => ([]),
    getIlluminaAvailablities: () => ([])
})

const ResourceProvider = (props: IProviderProps) => {
    const [resources, setResources] = useState<IResource[]>()
    const [availability, setAvailability] = useState<IAvailability[]>([])
    const [loading, setLoading] = useState<string>('Resources')
    const { get, post } = useContext(apiContext)
    const { isAuthed } = useContext(authContext)
    const [loadError, setLoadError] = useState(false)
    const [leaveStore, setLeaveStore] = useState<IEvent[]>([]);
    const [illuminaAvailability, setIlluminaAvailablity] = useState<IEvent[]>([]);

    // Keep internal backing info separate from IResource so no internal DTO shape leaks to consumers.
    const resourceBackingByIdRef = useRef<Record<string, ResourceBacking>>({});
    const resourcesRevisionRef = useRef(0)

    const getBetweenFilter = (sd: Date, ed: Date, startColName: string, endColName: string): string => {
        //add/remove a day to include boundaries events
        var sdStr = format(sd, 'yyyy-MM-dd')
        var edStr = format(ed, 'yyyy-MM-dd')
        //https://forums.asp.net/t/1912609.aspx?Validate+overlapping+of+two+date+range
        let filter = `Microsoft.Dynamics.CRM.Between(PropertyName='${startColName}',PropertyValues=["${sdStr}","${edStr}"])`
        filter += ` or Microsoft.Dynamics.CRM.Between(PropertyName='${endColName}',PropertyValues=["${sdStr}","${edStr}"])`;
        filter += ` or (${startColName} le ${sdStr} and ${endColName} ge ${edStr})`;
        return `(${filter})`;
    }

    useEffect(() => {
        const loadResources = async (start: Date, end: Date) => {
            try {
                const newResources: IResource[] = []
                const siteId = localStorage.getItem('siteid');

                //load leave requests for past and future six months
                let today = new Date();
                let leaveFilter = getBetweenFilter(subMonths(today, 6), addMonths(today, 6), 'msdyn_starttime', 'msdyn_endtime');
                let availabilityFilter = getBetweenFilter(subMonths(today, 6), addMonths(today, 6), 'illumina_startdate', 'illumina_enddate')
                const startDate = subDays(today, 14);
                const endDate = addDays(today, 14);
                if (!siteId) {
                    // handel error
                }
                const resourceCategories: { value: IBookableresourcecategoryResponse[] } = await get('/Entity/bookableresourcecategories', {
                    select: 'name,description,illumina_displaycolour',
                    filter: 'illumina_displaycolour ne null'
                });

                // Allow both Contact-backed (2) and User-backed (3) bookable resources.
                // In practice, a resource must still be explicitly associated to the current Site Location to appear.
                const resourceTypeFilter = `(resourcetype eq 2 or resourcetype eq 3)`;
                
                const req: { value: IResourceIngress[] } = await get('/Entity/bookableresources',
                    {
                        select: 'name,illumina_resourcesummary, resourcetype,illumina_minimumworkhourperpayroll,illumina_maximumhourspershift,_calendarid_value',
                        orderby: 'lastname desc',
                        filter: `statecode eq 0 and ${resourceTypeFilter} and illumina_BookableResource_illumina_sitelocation_il/any(o:o/illumina_sitelocationid eq '${siteId}')`,
                        // Expand both User and Contact so names show regardless of backing.
                        expand: `UserId($select=firstname,lastname,photourl),` +
                            `ContactId($select=firstname,lastname,emailaddress1),` +
                            `illumina_availability_BookableResource_bookableresource($filter=${availabilityFilter} and statuscode eq 390950002),` + //390950002 means approved
                            'bookableresource_bookableresourcecharacteristic_Resource($select=name,bookableresourcecharacteristicid,_ratingvalue_value,illumina_expirydate,_characteristic_value,statecode;$filter=statecode eq 0),' +
                            'illumina_BookableResource_illumina_sitelocation_il($select=illumina_name;$filter=illumina_sitelocationid eq ' + siteId + '),' +
                            'bookableresource_bookableresourcecategoryassn_Resource($select=_resourcecategory_value;$filter=statecode eq 0),' +
                            `msdyn_bookableresource_msdyn_timeoffrequest_Resource($filter=statecode eq 1 and _msdyn_approvedby_value ne null and (${leaveFilter}))`
                    })

                let illuminaAvailabilities: IEvent[] = [];
                
                req.value.forEach((res) => {
                    if (!res.illumina_BookableResource_illumina_sitelocation_il.find(location => location.illumina_sitelocationid === siteId)) return;

                    // Record backing type internally (no DTO shape leaks through `IResource`).
                    // `resourcetype`: 2=Contact, 3=User.
                    resourceBackingByIdRef.current[res.bookableresourceid] = (res.resourcetype === 2 ? "contact" : "systemuser");

                    let resource = {
                        personalNote: res.illumina_resourcesummary,
                        title: res.name,
                        id: res.bookableresourceid,
                        profileImageUrl: res.UserId?.photoUrl,
                        minimumWorkHoursPerPayroll: res.illumina_minimumworkhourperpayroll as number,
                        maximumWorkHourPerPayroll: res.illumina_maximumworkhourperpayroll as number,
                        maxHoursPerShift: res.illumina_maximumhourspershift,
                        calendarId: res._calendarid_value,
                        characteristics: res.bookableresource_bookableresourcecharacteristic_Resource
                            .map((ch): ICharacteristic => ({
                                name: ch.name,
                                id: ch.bookableresourcecharacteristicid,
                                ratingName: ch['_ratingvalue_value@OData.Community.Display.V1.FormattedValue'],
                                expiryDate: ch.illumina_expirydate,
                                characteristicId: ch['_characteristic_value']
                            })).sort((x, y) => x.name.localeCompare(y.name)),
                        siteLocation: res.illumina_BookableResource_illumina_sitelocation_il,
                        index: newResources.length,
                        categories: [],
                        approvedLeaves: [],
                        contractedHours: res.illumina_availability_BookableResource_bookableresource.filter(av => av.illumina_availabilitytype === AvailabilityType.ContractedHours && av.illumina_contractedhourstype === ContractedHoursType.Active)
                    } as IResource;

                    let availabilityContractedHours: IContractedHours[] = [];
                    const brAvailabilities: IEvent[] = [];
                    res.illumina_availability_BookableResource_bookableresource.forEach(y => {
                        if (y.illumina_availabilitytype === AvailabilityType.ContractedHours) {
                            availabilityContractedHours = availabilityContractedHours.concat(contractedHoursFormatter(y));
                        } else {
                            if (y.illumina_repeatpattern === RepeatPattern.Fortnightly) {
                                brAvailabilities.push(...availabilityFortnightlyFormatter(resource.id, y));
                            } else {
                                brAvailabilities.push(availabilityFormatter(resource.id, y));
                            }
                        }
                    });

                    if (availabilityContractedHours.length > 0) {
                        const contractedHoursEvents = groupAndCreateEvents(availabilityContractedHours, resource.id);
                        if (contractedHoursEvents.length > 0) {
                            illuminaAvailabilities = illuminaAvailabilities.concat(contractedHoursEvents);
                        }
                    }

                    

                    if (brAvailabilities.length > 0)
                        illuminaAvailabilities = illuminaAvailabilities.concat(brAvailabilities);
                    newResources.push(resource);
                    res.bookableresource_bookableresourcecategoryassn_Resource?.forEach(x => {
                        let rc = resourceCategories.value.find(y => y.bookableresourcecategoryid == x._resourcecategory_value);
                        if (rc != null)
                            resource.categories.push({
                                description: rc.description,
                                displayColour: rc.illumina_displaycolour,
                                id: rc.bookableresourcecategoryid,
                                name: rc.name
                            });
                    });
                    res.msdyn_bookableresource_msdyn_timeoffrequest_Resource.forEach(x => {
                        resource.approvedLeaves.push({ start: new Date(x.msdyn_starttime), end: new Date(x.msdyn_endtime), id: x.msdyn_timeoffrequestid });
                    })
                });
                setIlluminaAvailablity(illuminaAvailabilities);
                let leaves: IEvent[] = [];
                newResources.forEach(r => {
                    r.approvedLeaves.forEach(al => {
                        let e = leaveFormatter(al, r);
                        leaves.push(e);
                    })
                });
                setLeaveStore(leaves);

                // Ensure the special "Unassigned" resource is always the first row in the roster board.
                // Other resources remain sorted alphabetically by title.
                newResources.push({
                    title: "Unassigned",
                    id: "0",
                    // placeholder index; will be re-assigned below after sorting
                    index: -1,
                    characteristics: [],
                    categories: [],
                    approvedLeaves: []
                })

                // Keep "Unassigned" on top, then sort by name.
                newResources?.sort((a, b) => {
                    if (a.id === '0' && b.id !== '0') return -1;
                    if (a.id !== '0' && b.id === '0') return 1;
                    if (a.title > b.title) return 1;
                    if (a.title < b.title) return -1;
                    return 0
                }).forEach((resource, index) => {
                    resource.index = index;
                });



                setLoading('Calendars')

                // const expandCalendarRequest = {
                //     calendarIds: newResources.map(x => x.calendarId),
                //     start: start,
                //     end: end
                // }
                // const expandCalendarResponses: IExpandCalendarResponse[] = await post('/Rostering/expandcalendars', expandCalendarRequest);
                // expandCalendarResponses.forEach(r => {
                //     var br = newResources.find(nr => nr.calendarId == r.calendarId);
                //     if (br) {
                //         br.availabilities = r.availabilities.map((x: IExpandCalendarAvailabity) => {
                //             return { ...x, start: new Date(x.start), end: new Date(x.end) };
                //         });
                //     }
                // })

                setResources(newResources)
                setLoading('')
            } catch (e) {
                setLoadError(true)
            }

        }
        if ((!resources || resources.length < 1) && isAuthed) {
            let tempStart = subMonths(new Date(), 6);
            let tempEnd = addMonths(new Date(), 6);
            loadResources(tempStart, tempEnd)
        }
    }, [resources, isAuthed])

    const getResource = (id: string) => {
        if (!resources) {
            return null
        }
        return resources.find(res => res.id === id) ?? null
    }
    const getResources = useCallback(() => {
        return resources ?? []
    }, [resources])

    const getResourcesRevision = useCallback(() => {
        return resourcesRevisionRef.current // Incremented on resource change, to trigger effects when a resource is updated.
    }, [])

    const getLeaves = useCallback(() => {
        return leaveStore ?? []
    }, [resources])


    const getResourceCount = (): number => {
        return resources ? resources.length : 0
    }

    const overrideResources = (nResources: IResource[]) => {
        setResources(nResources)
    }

    const updateAvailability = (availability: IAvailability[]) => {
        setAvailability(availability)
    }

    const getAvailabilities = useCallback(() => {
        return availability ?? []
    }, [availability])

    const getAvailabilityByResource = (resourceId: string): EEventStatus => {
        const foundAvailability = availability.find(av => av.resourceId = resourceId)
        if (foundAvailability) {
            return foundAvailability.availability
        }
        return EEventStatus.valid

    }
    const getIlluminaAvailablities = useCallback((): IEvent[] => {
        return illuminaAvailability ?? [];
    }, [resources]);

    useEffect(() => {
        resourcesRevisionRef.current += 1 // Resource change occurred, increment revision to trigger effects
    }, [resources])

    useEffect(() => {
        resourcesRevisionRef.current += 1 // Availability change occurred, increment revision to trigger effects
    }, [availability])

    function groupAndCreateEvents(data: IContractedHours[], sourceId: string) {
        const groupedByDay = {};
        data.forEach(item => {
          const startDate = new Date(item.startDate);
          const endDate = new Date(item.endDate);
      
          const dayKey = format(startDate, 'yyyy-MM-dd'); 
          const eventId = `${item.id}_${dayKey}`; // Build a UI-only id, to ensure each day is unique and avoid ID collisions.
      
          // @ts-ignore
          if (!groupedByDay[dayKey]) {
            const start = startOfDay(startDate);
            const end = addMinutes(start, 30);
            // @ts-ignore
            groupedByDay[dayKey] = {
              start: start,
              end: end,
              id: eventId,
              editable: false,
              type: item.type,
              resourceEditable: false,
              events: [],
            };
          }
      
          // @ts-ignore
          groupedByDay[dayKey].events.push({
            ... item
          });
        });
      
        const events = Object.keys(groupedByDay).map(dayKey => {
        // @ts-ignore
          const dayData = groupedByDay[dayKey];
          let event = {
            resourceId: sourceId,
            title: `contractedHours`,
            id: dayData.id,
            display: 'contractedHours',
            description: '',
            editable: false,
            start: dayData.start,
            end: dayData.end,
            availabilityType: dayData.type,
            resourceEditable: false,
            extendedProps: {
                events: dayData.events 
            },
        }  as unknown as IEvent;
        return event;   
      });
      return events;
    }

    return (
        <resourcesContext.Provider value={{
            loading,
            getResource,
            getResourceCount,
            getAllResources: getResources,
            getResourcesRevision,
            setResources: overrideResources,
            getAvailabilities,
            updateAvailability,
            getAvailabilityByResource,
            getLeaves,
            getIlluminaAvailablities
        }}>
            {loadError ? (
                <div style={{
                    width: '100vw',
                    height: '100vh',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                    color: 'rgb(243, 112, 55)',
                    fontSize: '12px'
                }}>
                    <Icon iconName="error" style={{ fontSize: '24px' }} />
                    <p style={{ marginBottom: '0' }}>An error occurred whilst loading {loading.toLowerCase()}.</p>
                    <p>
                        Please <a
                            onClick={(e) => { e.preventDefault(); window.location.reload() }}
                            href={window.location.href}
                            style={{ color: 'rgb(243, 112, 55)', fontWeight: '500' }}
                        >
                            reload
                        </a> the page.
                    </p>
                </div>
            ) : (
                props.children
            )}
        </resourcesContext.Provider>
    )
}

export { ResourceProvider, resourcesContext }
